import { clientFetch } from '~/lib/.client/fetch';
import { CLIENT_ORIGIN } from '~/lib/constants';
import { prepareRequestBody } from '~/lib/hooks/useMessages/prepare-body';
import { getToken } from '~/lib/stores/token';
import type { Message } from '~/types/chat';
import type { OrganizationData } from '~/types/organization';
import { createScopedLogger } from '~/utils/logger';

const logger = createScopedLogger('API');

type TokenReloadResult =
  | {
      error: string;
    }
  | {
      sessionUrl: string;
    };

export async function reloadTokens(organization?: OrganizationData): Promise<TokenReloadResult> {
  const token = await getToken();

  const endpoint = organization
    ? `/api/organizations/${organization.provider}/${organization.slug}/bolt/tokens_reload`
    : '/api/bolt/tokens_reload';

  const url = new URL(endpoint, CLIENT_ORIGIN);

  url.searchParams.set('success_url', window.origin);
  url.searchParams.set('cancel_url', window.origin);

  try {
    const response = await clientFetch(url, {
      method: 'POST',
      headers: {
        authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    const result: { sessionUrl: string } = await response.json();

    return { sessionUrl: result.sessionUrl };
  } catch (error: unknown) {
    let errorMessage = 'Failed to reload tokens';

    if (error instanceof Error) {
      errorMessage = error.message;
    }

    return { error: errorMessage };
  }
}

export async function countTokens(chatMessages: Message[]) {
  const body = prepareRequestBody<'typed'>({ messages: chatMessages });

  try {
    const response = await clientFetch('/api/context-size', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      return undefined;
    }

    const { count } = await response.json<{ count?: number }>();

    return count;
  } catch (error) {
    logger.error(error);

    return undefined;
  }
}
